import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyAddress = { organisation: "", name: "", strasse: "", plz: "", ort: "", land: "Deutschland" };

const initialForm = {
  vorname: "",
  nachname: "",
  organisation: "",
  funktion: "",
  email: "",
  telefon: "",
  anzahl: "1",
  rechnung: { ...emptyAddress },
  abweichend: false,
  lieferung: { ...emptyAddress },
  nachricht: "",
  datenschutz: false,
  website: "",
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-navy-950/70 px-4 py-3 text-sm text-gray-100 placeholder-gray-600 transition-[border-color,box-shadow] duration-200 focus:border-blau focus:shadow-[0_0_0_3px_rgba(0,102,255,0.2)] focus:outline-none";

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-300">
      {label}
      {required && <span className="ml-1 text-blau-glow">*</span>}
    </label>
    {children}
    {error && (
      <p role="alert" data-testid={`${id}-error`} className="mt-1.5 text-xs text-red-400">
        {error}
      </p>
    )}
  </div>
);

const Fieldset = ({ legend, children }) => (
  <fieldset className="rounded-xl border border-white/8 bg-navy-800/40 p-6 sm:p-8">
    <legend className="px-2 font-mono text-xs uppercase tracking-[0.25em] text-blau-glow">
      {legend}
    </legend>
    <div className="grid gap-5 sm:grid-cols-2">{children}</div>
  </fieldset>
);

const AddressFields = ({ prefix, values, onChange, errors, requireBase }) => (
  <>
    <Field id={`${prefix}-organisation`} label="Organisation / Firma">
      <input id={`${prefix}-organisation`} data-testid={`${prefix}-organisation`} className={inputCls} value={values.organisation} onChange={onChange("organisation")} />
    </Field>
    <Field id={`${prefix}-name`} label="Vorname / Nachname">
      <input id={`${prefix}-name`} data-testid={`${prefix}-name`} className={inputCls} value={values.name} onChange={onChange("name")} />
    </Field>
    <Field id={`${prefix}-strasse`} label="Straße und Hausnummer" required={requireBase} error={errors[`${prefix}.strasse`]}>
      <input id={`${prefix}-strasse`} data-testid={`${prefix}-strasse`} className={inputCls} value={values.strasse} onChange={onChange("strasse")} />
    </Field>
    <div className="grid grid-cols-2 gap-5">
      <Field id={`${prefix}-plz`} label="PLZ" required={requireBase} error={errors[`${prefix}.plz`]}>
        <input id={`${prefix}-plz`} data-testid={`${prefix}-plz`} className={inputCls} value={values.plz} onChange={onChange("plz")} />
      </Field>
      <Field id={`${prefix}-ort`} label="Ort" required={requireBase} error={errors[`${prefix}.ort`]}>
        <input id={`${prefix}-ort`} data-testid={`${prefix}-ort`} className={inputCls} value={values.ort} onChange={onChange("ort")} />
      </Field>
    </div>
    <Field id={`${prefix}-land`} label="Land" required={requireBase} error={errors[`${prefix}.land`]}>
      <input id={`${prefix}-land`} data-testid={`${prefix}-land`} className={inputCls} value={values.land} onChange={onChange("land")} />
    </Field>
  </>
);

const OrderForm = () => {
  const [f, setF] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setF((p) => ({ ...p, [key]: e.target.value }));
  const setAddr = (addr, key) => (e) =>
    setF((p) => ({ ...p, [addr]: { ...p[addr], [key]: e.target.value } }));

  const validate = () => {
    const e = {};
    if (!f.vorname.trim()) e.vorname = "Bitte geben Sie Ihren Vornamen an.";
    if (!f.nachname.trim()) e.nachname = "Bitte geben Sie Ihren Nachnamen an.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
      e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    const n = Number(f.anzahl);
    if (!Number.isInteger(n) || n < 1)
      e.anzahl = "Bitte geben Sie eine positive ganze Zahl an.";
    if (!f.rechnung.strasse.trim()) e["rechnung.strasse"] = "Pflichtfeld.";
    if (!f.rechnung.plz.trim()) e["rechnung.plz"] = "Pflichtfeld.";
    if (!f.rechnung.ort.trim()) e["rechnung.ort"] = "Pflichtfeld.";
    if (!f.rechnung.land.trim()) e["rechnung.land"] = "Pflichtfeld.";
    if (f.abweichend) {
      if (!f.lieferung.strasse.trim()) e["lieferung.strasse"] = "Pflichtfeld.";
      if (!f.lieferung.plz.trim()) e["lieferung.plz"] = "Pflichtfeld.";
      if (!f.lieferung.ort.trim()) e["lieferung.ort"] = "Pflichtfeld.";
      if (!f.lieferung.land.trim()) e["lieferung.land"] = "Pflichtfeld.";
    }
    if (!f.datenschutz)
      e.datenschutz = "Bitte bestätigen Sie die Datenschutzhinweise.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/bestellung`, {
        vorname: f.vorname.trim(),
        nachname: f.nachname.trim(),
        organisation: f.organisation.trim(),
        funktion: f.funktion.trim(),
        email: f.email.trim(),
        telefon: f.telefon.trim(),
        anzahl: Number(f.anzahl),
        rechnung: f.rechnung,
        lieferadresse_abweichend: f.abweichend,
        lieferung: f.abweichend ? f.lieferung : null,
        nachricht: f.nachricht,
        datenschutz: f.datenschutz,
        website: f.website,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="bestellen"
      data-testid="order-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(0,102,255,0.14)_0%,rgba(10,15,29,0)_70%)]"
      />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          overline="Bestellung"
          title="BlaulichtPraxis bestellen"
          sub="Datenschutz verstehen. Sicherer entscheiden. Praxisnah umsetzen."
        />
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm sm:text-base text-gray-400 leading-relaxed max-w-2xl">
            Sie möchten BlaulichtPraxis – Datenschutz für sich, Ihre
            Organisation, Ihren Verband oder Ihre Ausbildungsstätte bestellen?
            Senden Sie uns Ihre Bestellanfrage direkt über das Formular.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="price-info"
            className="mt-8 flex flex-col gap-4 rounded-xl border border-blau/25 bg-blau/10 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7"
          >
            <div>
              <p className="font-display text-2xl font-extrabold text-white">
                29,95 €{" "}
                <span className="text-sm font-medium text-gray-400">
                  pro Exemplar
                </span>
              </p>
              <p className="mt-1 text-sm text-gray-400">
                zzgl. 3,95 € Versandkosten
              </p>
            </div>
            <p className="max-w-sm text-sm text-gray-400 leading-relaxed">
              Bei mehr als 10 Exemplaren fragen Sie unsere
              Mengenstaffelpreise bitte per E-Mail an{" "}
              <a
                href="mailto:kontakt@blaulichtpraxis.de"
                data-testid="price-bulk-mail"
                className="text-blau-glow underline underline-offset-4 hover:text-white"
              >
                kontakt@blaulichtpraxis.de
              </a>{" "}
              an.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {status === "success" ? (
            <div
              data-testid="order-success"
              className="mt-12 rounded-xl border border-blau/40 bg-blau/10 p-10 text-center"
            >
              <CheckCircle2 size={44} className="mx-auto text-blau-glow" aria-hidden="true" />
              <h3 className="mt-6 font-display text-2xl sm:text-3xl font-extrabold text-white">
                Vielen Dank für Ihre Bestellung.
              </h3>
              <p className="mt-4 text-base text-gray-300 max-w-xl mx-auto">
                Ihre Angaben wurden erfolgreich übermittelt. Wir melden uns
                bezüglich der weiteren Abwicklung bei Ihnen.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="order-form"
              className="mt-12 space-y-8"
            >
              <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={f.website}
                  onChange={set("website")}
                />
              </div>

              <Fieldset legend="Kontaktdaten">
                <Field id="vorname" label="Vorname" required error={errors.vorname}>
                  <input id="vorname" data-testid="input-vorname" className={inputCls} value={f.vorname} onChange={set("vorname")} autoComplete="given-name" />
                </Field>
                <Field id="nachname" label="Nachname" required error={errors.nachname}>
                  <input id="nachname" data-testid="input-nachname" className={inputCls} value={f.nachname} onChange={set("nachname")} autoComplete="family-name" />
                </Field>
                <Field id="organisation" label="Organisation / Firma">
                  <input id="organisation" data-testid="input-organisation" className={inputCls} value={f.organisation} onChange={set("organisation")} autoComplete="organization" />
                </Field>
                <Field id="funktion" label="Funktion / Position">
                  <input id="funktion" data-testid="input-funktion" className={inputCls} value={f.funktion} onChange={set("funktion")} autoComplete="organization-title" />
                </Field>
                <Field id="email" label="E-Mail-Adresse" required error={errors.email}>
                  <input id="email" type="email" data-testid="input-email" className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" />
                </Field>
                <Field id="telefon" label="Telefonnummer (optional)">
                  <input id="telefon" type="tel" data-testid="input-telefon" className={inputCls} value={f.telefon} onChange={set("telefon")} autoComplete="tel" />
                </Field>
              </Fieldset>

              <Fieldset legend="Bestellung">
                <Field id="anzahl" label="Anzahl gewünschter Exemplare" required error={errors.anzahl}>
                  <input
                    id="anzahl"
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    data-testid="input-anzahl"
                    className={`${inputCls} sm:max-w-[200px]`}
                    value={f.anzahl}
                    onChange={set("anzahl")}
                  />
                </Field>
              </Fieldset>

              <Fieldset legend="Rechnungsadresse">
                <AddressFields prefix="rechnung" values={f.rechnung} onChange={(k) => setAddr("rechnung", k)} errors={errors} requireBase />
              </Fieldset>

              <div className="rounded-xl border border-white/8 bg-navy-800/40 p-6 sm:p-8">
                <label className="flex cursor-pointer items-start gap-3" data-testid="toggle-lieferadresse-label">
                  <input
                    type="checkbox"
                    data-testid="checkbox-lieferadresse"
                    checked={f.abweichend}
                    onChange={(e) => setF((p) => ({ ...p, abweichend: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-navy-950 accent-[#0066FF]"
                  />
                  <span className="text-sm font-medium text-gray-200">
                    Lieferadresse weicht von Rechnungsadresse ab
                  </span>
                </label>

                <AnimatePresence initial={false}>
                  {f.abweichend && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="lieferadresse-fields">
                        <AddressFields prefix="lieferung" values={f.lieferung} onChange={(k) => setAddr("lieferung", k)} errors={errors} requireBase />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Fieldset legend="Nachricht">
                <div className="sm:col-span-2">
                  <Field id="nachricht" label="Anmerkungen zur Bestellung (optional)">
                    <textarea
                      id="nachricht"
                      rows={4}
                      data-testid="input-nachricht"
                      className={`${inputCls} resize-y`}
                      value={f.nachricht}
                      onChange={set("nachricht")}
                    />
                  </Field>
                </div>
              </Fieldset>

              <div>
                <label className="flex cursor-pointer items-start gap-3" data-testid="datenschutz-label">
                  <input
                    type="checkbox"
                    data-testid="checkbox-datenschutz"
                    checked={f.datenschutz}
                    onChange={(e) => setF((p) => ({ ...p, datenschutz: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-navy-950 accent-[#0066FF]"
                  />
                  <span className="text-sm text-gray-300">
                    Ich habe die{" "}
                    <Link to="/datenschutz" data-testid="datenschutz-link" className="text-blau-glow underline underline-offset-4 hover:text-white">
                      Datenschutzhinweise
                    </Link>{" "}
                    gelesen und stimme der Verarbeitung meiner Angaben zur
                    Bearbeitung meiner Bestellung zu. *
                  </span>
                </label>
                {errors.datenschutz && (
                  <p role="alert" data-testid="datenschutz-error" className="mt-1.5 text-xs text-red-400">
                    {errors.datenschutz}
                  </p>
                )}
              </div>

              {status === "error" && (
                <div
                  role="alert"
                  data-testid="order-error"
                  className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-5"
                >
                  <AlertCircle size={20} className="mt-0.5 shrink-0 text-red-400" aria-hidden="true" />
                  <p className="text-sm text-red-200">
                    Die Bestellung konnte leider nicht übermittelt werden. Bitte
                    versuchen Sie es erneut.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="order-submit-button"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-blau px-10 py-4 text-base font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:bg-blau-glow hover:text-navy-950 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-10px_rgba(0,102,255,0.8)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    Wird gesendet …
                  </>
                ) : (
                  <>
                    Bestellung absenden
                    <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500">
                Ihre Angaben werden ausschließlich zur Bearbeitung Ihrer Anfrage
                bzw. Bestellung verwendet.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default OrderForm;
